import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import SuperAdminNav from "../components/adNav";
import Time from "../components/time";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const Admin_Members_Approval = () => {
  
  const { memId } = useParams();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Selection states
  const [selectedRemove, setSelectedRemove] = useState([]);
  const [selectedView, setSelectedView] = useState([]);
  const [selectedAdd, setSelectedAdd] = useState([]);
  const [selectedEdit, setSelectedEdit] = useState([]);
  const [selectedDelete, setSelectedDelete] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://rfs2.infraconsoft.net.in/api/admin_aprrovalList?memId=${memId}`
      );

      const list = response?.data?.ListData || [];
      setData(list);
      setFilteredData(list);

      // Initialize permission selections from current flags
      const initView = list
        .filter((x) => String(x.fld_view ?? "").trim() !== "0")
        .map((x) => x.SlNo);
      const initAdd = list
        .filter((x) => String(x.fld_entry ?? "").trim() !== "0")
        .map((x) => x.SlNo);
      const initEdit = list
        .filter((x) => String(x.fld_edit ?? "").trim() !== "0")
        .map((x) => x.SlNo);
      const initDelete = list
        .filter((x) => String(x.fld_delete ?? "").trim() !== "0")
        .map((x) => x.SlNo);

      setSelectedRemove([]); // start removal selection empty
      setSelectedView(initView);
      setSelectedAdd(initAdd);
      setSelectedEdit(initEdit);
      setSelectedDelete(initDelete);
    } catch (error) {
      console.error("Error fetching ListData:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term === "") {
      setFilteredData(data);
      setCurrentPage(1);
      return;
    }

    const filtered = data.filter((item) =>
      String(item.fld_department || "").toLowerCase().includes(term)
    );

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredData.length / itemsPerPage)),
    [filteredData.length, itemsPerPage]
  );
  const paginate = (pageNumber) =>
    setCurrentPage(Math.min(Math.max(pageNumber, 1), totalPages));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Helpers
  const toggleInArray = (setter, arr, slNo, checked) => {
    if (checked) {
      if (!arr.includes(slNo)) setter([...arr, slNo]);
    } else {
      setter(arr.filter((id) => id !== slNo));
    }
  };

  // Per-row checkbox handlers
  const handleRemoveCheckboxChange = (e, slNo) =>
    toggleInArray(setSelectedRemove, selectedRemove, slNo, e.target.checked);
  const handleViewCheckboxChange = (e, slNo) =>
    toggleInArray(setSelectedView, selectedView, slNo, e.target.checked);
  const handleAddCheckboxChange = (e, slNo) =>
    toggleInArray(setSelectedAdd, selectedAdd, slNo, e.target.checked);
  const handleEditCheckboxChange = (e, slNo) =>
    toggleInArray(setSelectedEdit, selectedEdit, slNo, e.target.checked);
  const handleDeleteCheckboxChange = (e, slNo) =>
    toggleInArray(setSelectedDelete, selectedDelete, slNo, e.target.checked);

  // Select/Clear for the filtered set
  const allFilteredSlNos = useMemo(
    () => filteredData.map((x) => x.SlNo),
    [filteredData]
  );

  const selectAllGroup = (group) => {
    switch (group) {
      case "remove":
        setSelectedRemove(allFilteredSlNos);
        break;
      case "view":
        setSelectedView(allFilteredSlNos);
        break;
      case "add":
        setSelectedAdd(allFilteredSlNos);
        break;
      case "edit":
        setSelectedEdit(allFilteredSlNos);
        break;
      case "delete":
        setSelectedDelete(allFilteredSlNos);
        break;
      default:
        break;
    }
  };

  const clearAllGroup = (group) => {
    switch (group) {
      case "remove":
        setSelectedRemove([]);
        break;
      case "view":
        setSelectedView([]);
        break;
      case "add":
        setSelectedAdd([]);
        break;
      case "edit":
        setSelectedEdit([]);
        break;
      case "delete":
        setSelectedDelete([]);
        break;
      default:
        break;
    }
  };

  // Update API calls
  const handleUpdateView = async () => {
    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_viewPermission", {
        memId, 
        selectedView: selectedView,
      });

      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleUpdateAdd = async () => {
    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_addPermission", {
        memId, 
        selectedView: selectedAdd,
      });

      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleUpdateEdit = async () => {
    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_editPermission", {
        memId,
        selectedView: selectedEdit,
      });

      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleUpdateDelete = async () => {
    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_deletePermission", {
        memId,  
        selectedView: selectedDelete,
      });

      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  // Remove selected
  const handleRemoveSelected = async () => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Selected members will be removed!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete them!",
    });

    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.delete("https://rfs2.infraconsoft.net.in/api/Admin_deleteMembers", {
        data: { slNos: selectedRemove },
      });

      const updatedData = data.filter((item) => !selectedRemove.includes(item.SlNo));
      setData(updatedData);
      setFilteredData(updatedData);

      // Clean any now-removed IDs from permission selections
      setSelectedView((prev) => prev.filter((id) => !selectedRemove.includes(id)));
      setSelectedAdd((prev) => prev.filter((id) => !selectedRemove.includes(id)));
      setSelectedEdit((prev) => prev.filter((id) => !selectedRemove.includes(id)));
      setSelectedDelete((prev) => prev.filter((id) => !selectedRemove.includes(id)));

      setSelectedRemove([]);
      Swal.fire("Deleted!", "Members have been deleted.", "success");
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire("Error", "Failed to delete members", "error");
    }
  };

  return (
    <div id="admin-container">
      <SuperAdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Admin Members Approval</h2>
            <Link to={`/admin/admin_members_list`}>
              <p className="back-button">
                <i className="fa-solid fa-backward"></i>Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        {/* Search Filter */}
        <div className="search-filter">
          <p>Search: </p>
          <input
            type="text"
            placeholder="Search by Department"
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        

        {/* Table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>
                  <div style={{ display: "grid", gap: 6 }}>
                    <button className="remove-btn" onClick={handleRemoveSelected}>
                      <i className="fa-solid fa-trash"></i> Remove
                    </button>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="update-btn" onClick={() => selectAllGroup("remove")}>Select All</button>
                      <button className="update-btn" onClick={() => clearAllGroup("remove")}>Clear</button>
                    </div>
                  </div>
                </th>

                <th>MemberId</th>
                <th>User Name</th>
                <th>Department</th>

                <th>
                  <div style={{ display: "grid", gap: 6 }}>
                    <button className="update-btn" onClick={handleUpdateView}>
                      <i className="fa-solid fa-rotate"></i> View
                    </button>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="update-btn" onClick={() => selectAllGroup("view")}>Select All</button>
                      <button className="update-btn" onClick={() => clearAllGroup("view")}>Clear</button>
                    </div>
                  </div>
                </th>

                <th>
                  <div style={{ display: "grid", gap: 6 }}>
                    <button className="update-btn" onClick={handleUpdateAdd}>
                      <i className="fa-solid fa-rotate"></i> Entry
                    </button>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="update-btn" onClick={() => selectAllGroup("add")}>Select All</button>
                      <button className="update-btn" onClick={() => clearAllGroup("add")}>Clear</button>
                    </div>
                  </div>
                </th>

                <th>
                  <div style={{ display: "grid", gap: 6 }}>
                    <button className="update-btn" onClick={handleUpdateEdit}>
                      <i className="fa-solid fa-rotate"></i> Edit
                    </button>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="update-btn" onClick={() => selectAllGroup("edit")}>Select All</button>
                      <button className="update-btn" onClick={() => clearAllGroup("edit")}>Clear</button>
                    </div>
                  </div>
                </th>

                <th>
                  <div style={{ display: "grid", gap: 6 }}>
                    <button className="update-btn" onClick={handleUpdateDelete}>
                      <i className="fa-solid fa-rotate"></i> Delete
                    </button>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="update-btn" onClick={() => selectAllGroup("delete")}>Select All</button>
                      <button className="update-btn" onClick={() => clearAllGroup("delete")}>Clear</button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((item, index) => (
                  <tr key={item.SlNo ?? index}>
                    <td>{indexOfFirstItem + index + 1}</td>

                    {/* Remove checkbox */}
                    <td>
                      <input
                        type="checkbox"
                        className="admin-checkbox-input"
                        value={item.SlNo}
                        checked={selectedRemove.includes(item.SlNo)}
                        onChange={(e) => handleRemoveCheckboxChange(e, item.SlNo)}
                      />
                    </td>

                    <td>{item.fld_memberid}</td>
                    <td>{item.fld_username}</td>
                    <td>{item.fld_department}</td>

                    {/* View */}
                    <td>
                      <input
                        type="checkbox"
                        value={item.SlNo}
                        className="admin-checkbox-input view"
                        checked={selectedView.includes(item.SlNo)}
                        onChange={(e) => handleViewCheckboxChange(e, item.SlNo)}
                      />
                    </td>

                    {/* Entry */}
                    <td>
                      <input
                        type="checkbox"
                        value={item.SlNo}
                        className="admin-checkbox-input add"
                        checked={selectedAdd.includes(item.SlNo)}
                        onChange={(e) => handleAddCheckboxChange(e, item.SlNo)}
                      />
                    </td>

                    {/* Edit */}
                    <td>
                      <input
                        type="checkbox"
                        value={item.SlNo}
                        className="admin-checkbox-input edit"
                        checked={selectedEdit.includes(item.SlNo)}
                        onChange={(e) => handleEditCheckboxChange(e, item.SlNo)}
                      />
                    </td>

                    {/* Delete */}
                    <td>
                      <input
                        type="checkbox"
                        value={item.SlNo}
                        className="admin-checkbox-input delete"
                        checked={selectedDelete.includes(item.SlNo)}
                        onChange={(e) => handleDeleteCheckboxChange(e, item.SlNo)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          <button onClick={() => paginate(1)} className={currentPage === 1 ? "active" : ""}>
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, idx) => {
              const pageNumber = currentPage + idx;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin_Members_Approval;
